import { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { CalendarCheck, Lock, LogOut, Phone, RefreshCw } from "lucide-react";
import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import Seo from "@/components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "aph_admin_token";

function formatError(detail) {
  if (!detail) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && e.msg) || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const loadStats = useCallback(async (t) => {
    setRefreshing(true);
    try {
      const res = await fetch(`${API}/admin/stats`, {
        headers: { Authorization: `Bearer ${t}` },
      });
      if (res.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
        return;
      }
      setStats(await res.json());
    } finally {
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    if (!token) return;
    loadStats(token);
    const id = setInterval(() => loadStats(token), 30000);
    return () => clearInterval(id);
  }, [token, loadStats]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await fetch(`${API}/admin/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(formatError(data.detail));
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setStats(null);
  };

  if (!token) {
    return (
      <main data-testid="admin-login-page" className="bg-slate-50/70">
        <Seo title="Admin Login | Advantage Primary Health" description="Admin dashboard login." />
        <div className="mx-auto flex max-w-7xl items-center justify-center px-4 py-24 sm:px-6 md:py-32">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="w-full max-w-md rounded-3xl border border-slate-100 bg-white p-8 shadow-lift sm:p-10"
          >
            <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-redlight">
              <Lock className="h-5 w-5 text-brand-red" />
            </span>
            <h1 className="mt-5 font-display text-2xl font-bold tracking-tight text-brand-navy">
              Admin Dashboard
            </h1>
            <p className="mt-2 text-sm text-slate-500">
              Sign in to view Book Online and Call click counts.
            </p>
            <form onSubmit={handleLogin} className="mt-7 space-y-4">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email"
                data-testid="admin-login-email-input"
                className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none transition-colors focus:border-brand-red"
              />
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password"
                data-testid="admin-login-password-input"
                className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none transition-colors focus:border-brand-red"
              />
              {error && (
                <p data-testid="admin-login-error" className="text-sm font-medium text-brand-red">
                  {error}
                </p>
              )}
              <button
                type="submit"
                disabled={loading}
                data-testid="admin-login-submit-btn"
                className="w-full rounded-full bg-brand-red px-6 py-3.5 text-sm font-semibold text-white shadow-red transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-brand-redhover disabled:opacity-60"
              >
                {loading ? "Signing in…" : "Sign In"}
              </button>
            </form>
          </motion.div>
        </div>
      </main>
    );
  }

  return (
    <main data-testid="admin-dashboard" className="bg-slate-50/70">
      <Seo title="Admin Dashboard | Advantage Primary Health" description="Conversion click dashboard." />
      <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-12">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-brand-red">
              Live Conversions
            </p>
            <h1 className="mt-2 font-display text-2xl font-bold tracking-tight text-brand-navy sm:text-3xl">
              Click Dashboard
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => loadStats(token)}
              data-testid="admin-refresh-btn"
              className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-5 py-2.5 text-sm font-medium text-slate-700 transition-colors hover:border-slate-900"
            >
              <RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
              Refresh
            </button>
            <button
              type="button"
              onClick={logout}
              data-testid="admin-logout-btn"
              className="inline-flex items-center gap-2 rounded-full bg-brand-navy px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-brand-navydeep"
            >
              <LogOut className="h-4 w-4" />
              Log Out
            </button>
          </div>
        </div>

        {!stats ? (
          <p data-testid="admin-loading" className="mt-16 text-center text-sm text-slate-500">
            Loading stats…
          </p>
        ) : (
          <>
            <div className="mt-8 grid gap-6 sm:grid-cols-2">
              {[
                {
                  testid: "stat-book-clicks",
                  label: "“Book Online” clicks · unique visitors",
                  icon: CalendarCheck,
                  iconCls: "bg-brand-redlight text-brand-red",
                  total: stats.totals.book_online_click,
                  today: stats.today.book_online_click,
                  week: stats.week.book_online_click,
                  month: stats.month.book_online_click,
                  raw: stats.raw_totals.book_online_click,
                },
                {
                  testid: "stat-call-clicks",
                  label: "Call clicks · unique visitors",
                  icon: Phone,
                  iconCls: "bg-brand-navy text-white",
                  total: stats.totals.call_click,
                  today: stats.today.call_click,
                  week: stats.week.call_click,
                  month: stats.month.call_click,
                  raw: stats.raw_totals.call_click,
                },
              ].map((card) => (
                <div
                  key={card.testid}
                  data-testid={card.testid}
                  className="rounded-3xl border border-slate-100 bg-white p-8 shadow-soft"
                >
                  <div className="flex items-center justify-between">
                    <span
                      className={`flex h-12 w-12 items-center justify-center rounded-2xl ${card.iconCls}`}
                    >
                      <card.icon className="h-5 w-5" />
                    </span>
                    <span className="font-mono text-xs uppercase tracking-[0.2em] text-slate-400">
                      All time
                    </span>
                  </div>
                  <p className="mt-6 font-display text-5xl font-extrabold text-brand-navy">
                    {card.total}
                  </p>
                  <p className="mt-2 text-sm font-medium text-slate-500">{card.label}</p>
                  <div className="mt-6 grid grid-cols-3 divide-x divide-slate-200 rounded-2xl bg-slate-50 py-4 text-center">
                    {[
                      { label: "Today", value: card.today, testid: `${card.testid}-today` },
                      { label: "This Week", value: card.week, testid: `${card.testid}-week` },
                      { label: "This Month", value: card.month, testid: `${card.testid}-month` },
                    ].map((period) => (
                      <div key={period.label} data-testid={period.testid}>
                        <p className="font-display text-xl font-bold text-brand-navy">
                          {period.value}
                        </p>
                        <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">
                          {period.label}
                        </p>
                      </div>
                    ))}
                  </div>
                  <p
                    data-testid={`${card.testid}-raw`}
                    className="mt-4 text-xs text-slate-400"
                  >
                    {card.raw} total taps including repeat clicks from the same visitor
                  </p>
                </div>
              ))}
            </div>

            <div
              data-testid="trend-chart-section"
              className="mt-8 rounded-3xl border border-slate-100 bg-white p-8 shadow-soft"
            >
              <div className="flex flex-wrap items-center justify-between gap-3">
                <h2 className="font-display text-lg font-semibold text-brand-navy">
                  Last 30 days
                </h2>
                <div className="flex items-center gap-5 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">
                  <span className="flex items-center gap-1.5">
                    <span className="h-2.5 w-2.5 rounded-sm bg-brand-red" /> Book Online
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="h-2.5 w-2.5 rounded-sm bg-brand-navy" /> Call
                  </span>
                </div>
              </div>
              <div className="mt-6 h-64 w-full" data-testid="trend-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={stats.daily}
                    margin={{ top: 4, right: 4, left: -18, bottom: 0 }}
                    barGap={2}
                  >
                    <XAxis
                      dataKey="label"
                      tick={{ fontSize: 10, fill: "#94a3b8" }}
                      tickLine={false}
                      axisLine={false}
                      interval={4}
                    />
                    <YAxis
                      allowDecimals={false}
                      tick={{ fontSize: 10, fill: "#94a3b8" }}
                      tickLine={false}
                      axisLine={false}
                      width={30}
                    />
                    <Tooltip
                      cursor={{ fill: "rgba(31,42,55,0.05)" }}
                      contentStyle={{
                        borderRadius: 12,
                        border: "1px solid #E2E8F0",
                        fontSize: 12,
                      }}
                    />
                    <Bar
                      dataKey="book_online_click"
                      name="Book Online"
                      fill="#C8102E"
                      radius={[3, 3, 0, 0]}
                    />
                    <Bar
                      dataKey="call_click"
                      name="Call"
                      fill="#1F2A37"
                      radius={[3, 3, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="mt-8 grid gap-6 lg:grid-cols-2">
              <div className="rounded-3xl border border-slate-100 bg-white p-8 shadow-soft">
                <h2 className="font-display text-lg font-semibold text-brand-navy">
                  Unique clicks by page
                </h2>
                {stats.by_page.length === 0 ? (
                  <p data-testid="by-page-empty" className="mt-4 text-sm text-slate-500">
                    No clicks recorded yet.
                  </p>
                ) : (
                  <ul data-testid="by-page-list" className="mt-4 divide-y divide-slate-100">
                    {stats.by_page.map((row) => (
                      <li
                        key={row.page_path}
                        className="flex items-center justify-between py-3 text-sm"
                      >
                        <span className="font-mono text-xs text-slate-600">{row.page_path}</span>
                        <span className="flex gap-4 font-medium text-slate-700">
                          <span className="text-brand-red">{row.book_online_click} book</span>
                          <span>{row.call_click} call</span>
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div className="rounded-3xl border border-slate-100 bg-white p-8 shadow-soft">
                <h2 className="font-display text-lg font-semibold text-brand-navy">
                  Recent clicks
                </h2>
                {stats.recent.length === 0 ? (
                  <p data-testid="recent-empty" className="mt-4 text-sm text-slate-500">
                    No activity yet.
                  </p>
                ) : (
                  <ul data-testid="recent-list" className="mt-4 divide-y divide-slate-100">
                    {stats.recent.map((ev, i) => (
                      <li key={i} className="flex items-center justify-between gap-3 py-3 text-sm">
                        <span
                          className={`inline-flex items-center gap-1.5 font-medium ${
                            ev.event === "book_online_click" ? "text-brand-red" : "text-brand-navy"
                          }`}
                        >
                          {ev.event === "book_online_click" ? (
                            <CalendarCheck className="h-3.5 w-3.5" />
                          ) : (
                            <Phone className="h-3.5 w-3.5" />
                          )}
                          {ev.event === "book_online_click" ? "Book Online" : "Call"}
                        </span>
                        <span className="font-mono text-[11px] text-slate-400">
                          {ev.page_path} · {new Date(ev.timestamp).toLocaleString()}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            <div
              data-testid="monthly-section"
              className="mt-8 rounded-3xl border border-slate-100 bg-white p-8 shadow-soft"
            >
              <h2 className="font-display text-lg font-semibold text-brand-navy">
                Month by month
              </h2>
              {stats.monthly.length === 0 ? (
                <p data-testid="monthly-empty" className="mt-4 text-sm text-slate-500">
                  No activity yet.
                </p>
              ) : (
                <div className="mt-4 overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-100 text-left font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">
                        <th className="py-3 pr-4 font-medium">Month</th>
                        <th className="py-3 pr-4 text-right font-medium">Book Online</th>
                        <th className="py-3 text-right font-medium">Call</th>
                      </tr>
                    </thead>
                    <tbody data-testid="monthly-list">
                      {stats.monthly.map((m) => (
                        <tr
                          key={m.month}
                          data-testid={`monthly-row-${m.month}`}
                          className="border-b border-slate-50 last:border-0"
                        >
                          <td className="py-3.5 pr-4 font-medium text-slate-700">{m.label}</td>
                          <td className="py-3.5 pr-4 text-right font-semibold text-brand-red">
                            {m.book_online_click}
                          </td>
                          <td className="py-3.5 text-right font-semibold text-brand-navy">
                            {m.call_click}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <p className="mt-8 text-xs leading-relaxed text-slate-400">
              Counts update automatically every 30 seconds. Full analytics (sources, devices,
              session duration) live in your Google Analytics dashboard.
            </p>
          </>
        )}
      </div>
    </main>
  );
}
